import { createAgent } from '../sdk/AgentBuilder';
import { PublicistService } from '../../publicist/PublicistService';
import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import { ImageGeneration } from '@/services/image/ImageGenerationService';
import { StorageService } from '@/services/StorageService';
import { logger } from '@/utils/logger';
import systemPrompt from '@agents/publicist/prompt.md?raw';
import { UniversalTools } from '../tools/UniversalTools';
import { importWithRetry } from '@/utils/dynamicImport';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';
import { McpTools } from '../tools/McpTools';



const publicistRetrievalConfig = {
    'press_releases': { path: 'press_releases', requiresUserIdFilter: true },
    'email_pitches': { path: 'email_pitches', requiresUserIdFilter: true },
    'publicist_campaigns': { path: 'publicist_campaigns', requiresUserIdFilter: true },
    'publicist_contacts': { path: 'publicist_contacts', requiresUserIdFilter: true }
};
const publicistRetrievalTools = buildDomainRetrievalTools('Publicist', publicistRetrievalConfig);
const publicistRetrievalDeclarations = buildDomainRetrievalDeclarations('Publicist', publicistRetrievalConfig);

export const PublicistAgent = createAgent('publicist')
    .withName('Publicist Director')
    .withDescription('Manages public relations and media communications.')
    .withColor('bg-pink-500')
    .withCategory('manager')
    .withSystemPrompt(systemPrompt)
    .withTool({
        functionDeclarations: [{
            name: "create_campaign",
            description: "Create a new publicity campaign in the database.",
            parameters: {
                type: "OBJECT",
                properties: {
                    userId: { type: "STRING", description: "The ID of the user." },
                    title: { type: "STRING", description: "Campaign Title." },
                    artist: { type: "STRING", description: "Artist Name." },
                    type: { type: "STRING", enum: ["Album", "Single", "Tour"], description: "Type of campaign." },
                    focus: { type: "STRING", description: "Main focus or goal." }
                },
                required: ["userId", "title", "artist", "type"]
            }
        }]
    }, async (args: { userId: string, title: string, artist: string, type: 'Album' | 'Single' | 'Tour', focus: string }) => {
        /**
         * Create a new publicity campaign in the database.
         */
        try {
            const startDate = new Date().toISOString().split('T')[0];
            await PublicistService.addCampaign(args.userId, {
                title: args.title,
                artist: args.artist,
                type: args.type,
                status: 'Draft',
                releaseDate: startDate ?? new Date().toISOString().split('T')[0]!,
                progress: 0,
                openRate: 0,
                budget: 0
            });
            return {
                success: true,
                data: {
                    message: `Campaign "${args.title}" created successfully in Draft mode.`,
                    status: "Draft"
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: `Failed to create campaign: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "write_press_release",
            description: "Write a formal press release.",
            parameters: {
                type: "OBJECT",
                properties: {
                    headline: { type: "STRING", description: "The major news headline." },
                    company_name: { type: "STRING", description: "Company Name." },
                    key_points: { type: "ARRAY", description: "List of key facts.", items: { type: "STRING" } },
                    contact_info: { type: "STRING", description: "Media contact details." }
                },
                required: ["headline", "company_name"]
            }
        }]
    }, async (args: { headline: string, company_name: string, key_points?: string[], contact_info?: string }) => {
        /**
         * Write a formal press release using AI.
         */
        const missing = [];
        if (!args.key_points || args.key_points.length === 0) missing.push("Key Points");
        if (!args.contact_info) missing.push("Contact Info");

        const prompt = `Write a formal press release for ${args.company_name}.
        Headline: ${args.headline}
        Key Points: ${(args.key_points || []).join(', ')}
        Contact Info: ${args.contact_info}
        
        Format as a standard press release.`;

        try {
            const content = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
            return {
                success: true,
                data: {
                    generated_content: content || "Failed to generate content.",
                    validation: {
                        is_complete: missing.length === 0,
                        missing_fields: missing,
                        word_count: content ? content.split(/\s+/).length : 0
                    }
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: message };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "generate_crisis_response",
            description: "Generate a response to a PR crisis or negative feedback.",
            parameters: {
                type: "OBJECT",
                properties: {
                    issue: { type: "STRING", description: "The negative event or comment." },
                    sentiment: { type: "STRING", description: "The current public sentiment." },
                    platform: { type: "STRING", description: "Where to post (Twitter, Email, etc)." }
                },
                required: ["issue"]
            }
        }]
    }, async (args: { issue: string, sentiment?: string, platform?: string }) => {
        /**
         * Generate a strategic response to a PR crisis.
         */
        const prompt = `Generate a crisis response for a public relations issue.
        Issue: ${args.issue}
        Sentiment: ${args.sentiment || 'Negative'}
        Platform: ${args.platform || 'General'}
        
        Strategy: Acknowledge, Empathize, Redirect. Keep it professional.`;

        try {
            const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
            return {
                success: true,
                data: {
                    strategy: "Acknowledge, Empathize, Redirect",
                    draft_response: response
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: message };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "generate_social_post",
            description: "Generate reviewable social media draft copy without saving or posting it.",
            parameters: {
                type: "OBJECT",
                properties: {
                    platform: { type: "STRING", description: "Platform (Twitter, LinkedIn, etc)." },
                    topic: { type: "STRING", description: "What the post is about." },
                    tone: { type: "STRING", description: "Desired tone." }
                },
                required: ["platform", "topic"]
            }
        }]
    }, async (args: { platform: string, topic: string, tone?: string }) => {
        /**
         * Generate a social media post with hashtags.
         */
        const prompt = `Write a social media post for ${args.platform}.
        Topic: ${args.topic}
        Tone: ${args.tone || 'Excited'}
        Include relevant hashtags.`;

        try {
            const post = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
            return {
                success: true,
                data: {
                    post_text: post
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: message };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "indii_image_gen",
            description: "Generate visual assets for press kits or social media.",
            parameters: {
                type: "OBJECT",
                properties: {
                    prompt: { type: "STRING", description: "Image description." },
                    style: { type: "STRING", description: "Art style (Photorealistic, Cinematic)." },
                    aspect_ratio: { type: "STRING", description: "1:1, 16:9, 9:16" }
                },
                required: ["prompt"]
            }
        }]
    }, async (args: { prompt: string, style?: string, aspect_ratio?: string }) => {
        /**
         * Generate visual assets using the real ImageGenerationService.
         */
        try {
            const results = await ImageGeneration.generateImages({
                prompt: args.prompt,
                aspectRatio: args.aspect_ratio || '1:1',
            });

            if (results && results.length > 0) {
                const firstResult = results[0]!;
                return {
                    success: true,
                    data: {
                        message: `Image generated successfully.`,
                        imageUrl: firstResult.url,
                        imageId: firstResult.id,
                        prompt: args.prompt,
                    }
                };
            }

            return {
                success: false,
                error: 'Image generation returned no result.'
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error('[PublicistAgent] Image generation failed:', error);
            return { success: false, error: `Image generation failed: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "web_extract",
            description: "Read bounded text from a public web page. This tool cannot log in, click, type, or submit forms.",
            parameters: {
                type: "OBJECT",
                properties: {
                    url: { type: "STRING", description: "Public HTTP(S) URL to read." }
                },
                required: ["url"]
            }
        }]
    }, UniversalTools.web_extract)
    .withTool({
        functionDeclarations: [{
            name: "credential_vault",
            description: "Securely retrieve passwords for social accounts.",
            parameters: {
                type: "OBJECT",
                properties: {
                    action: { type: "STRING", description: "retrieve" },
                    service: { type: "STRING", description: "Service name (e.g. Twitter)" }
                },
                required: ["action", "service"]
            }
        }]
    }, UniversalTools.credential_vault)
    .withTool({
        functionDeclarations: [{
            name: "generate_pdf",
            description: "Automatically generate a professional PDF document (e.g., Press Release, EPK).",
            parameters: {
                type: "OBJECT",
                properties: {
                    title: { type: "STRING", description: "Document title" },
                    content: { type: "STRING", description: "Document content in markdown or plain text" }
                },
                required: ["title", "content"]
            }
        }]
    }, async (args: { title: string, content: string }) => {
        /**
         * Generate a real PDF document using HTML-to-Blob conversion,
         * then upload to Firebase Storage and return a real download URL.
         */
        try {
            // Build a styled HTML document
            const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${args.title}</title>
<style>
  body { font-family: 'Georgia', serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
  h1 { font-size: 24px; border-bottom: 2px solid #333; padding-bottom: 8px; }
  p { line-height: 1.8; margin-bottom: 16px; }
  .header { text-align: center; margin-bottom: 40px; }
  .header h1 { border: none; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ccc; font-size: 12px; color: #666; }
</style>
</head>
<body>
  <div class="header">
    <h1>${args.title}</h1>
    <p style="color: #666; font-size: 14px;">Generated by indii Publicist • ${new Date().toLocaleDateString('en-US')}</p>
  </div>
  <div class="content">
    ${args.content.split('\n').map(line => `<p>${line}</p>`).join('')}
  </div>
  <div class="footer">
    <p>© ${new Date().getFullYear()} New Detroit Music LLC. All rights reserved.</p>
  </div>
</body>
</html>`;

            // Create a Blob from the HTML content
            const blob = new Blob([htmlContent], { type: 'text/html' });
            const file = new File([blob], `${args.title.replace(/[^a-zA-Z0-9]/g, '_')}.html`, { type: 'text/html' });

            // Upload to Firebase Storage
            const storagePath = `publicist/documents/${Date.now()}_${file.name}`;
            const downloadUrl = await StorageService.uploadFile(file, storagePath);

            return {
                success: true,
                data: {
                    message: `Document "${args.title}" generated and uploaded successfully.`,
                    download_url: downloadUrl,
                    format: 'html',
                    size_bytes: blob.size,
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error('[PublicistAgent] PDF generation failed:', error);
            return { success: false, error: `Document generation failed: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "generate_live_epk",
            description: "Generates a dynamic public link (indii.os/artist/epk) that reflects the latest brand kit, bio, and approved press shots.",
            parameters: {
                type: "OBJECT",
                properties: {
                    artistName: { type: "STRING", description: "The name of the artist." },
                    shortBio: { type: "STRING", description: "A concise 1-paragraph bio." },
                    pressShotUrls: { type: "ARRAY", items: { type: "STRING" }, description: "List of URLs for approved high-res press photos." },
                    featuredTracks: { type: "ARRAY", items: { type: "STRING" }, description: "List of Spotify/Apple track IDs or indii audio URLs." },
                    contactEmail: { type: "STRING", description: "Media contact email." }
                },
                required: ["artistName", "shortBio", "pressShotUrls", "contactEmail"]
            }
        }]
    }, async (args: { artistName: string, shortBio: string, pressShotUrls: string[], featuredTracks?: string[], contactEmail: string }) => {
        try {
            const { useStore } = await importWithRetry(() => import('@/core/store'));
            const { epkGeneratorService } = await importWithRetry(() => import('../../marketing/EPKGeneratorService'));
            
            // Perform real store update
            const store = useStore.getState();
            store.setUserProfile({
                ...store.userProfile,
                displayName: args.artistName,
                bio: args.shortBio,
                email: args.contactEmail,
                brandKit: {
                    colors: store.userProfile.brandKit?.colors || ['#000000', '#ffffff'],
                    fonts: store.userProfile.brandKit?.fonts || 'Inter',
                    brandDescription: store.userProfile.brandKit?.brandDescription || '',
                    negativePrompt: store.userProfile.brandKit?.negativePrompt || '',
                    socials: store.userProfile.brandKit?.socials || {},
                    brandAssets: args.pressShotUrls.map(url => ({ url, description: 'Press shot uploaded via Publicist EPK generator' })),
                    referenceImages: store.userProfile.brandKit?.referenceImages || [],
                    releaseDetails: store.userProfile.brandKit?.releaseDetails || {
                        title: '',
                        type: 'Single',
                        artists: args.artistName,
                        genre: '',
                        mood: '',
                        themes: '',
                        lyrics: ''
                    }
                }
            });

            const epkUrl = epkGeneratorService.getPublicEPKUrl(args.artistName);

            return {
                success: true,
                data: {
                    message: `Live EPK generated for ${args.artistName} and updated in user profile.`,
                    epkUrl: epkUrl,
                    status: 'Published'
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: `Failed to generate EPK: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "pitch_media",
            description: "Generate personalized pitch emails for press outlets (blogs, magazines, podcasts) based on a press release or release info. Each outlet gets a tailored angle. Returns up to 8 ready-to-send pitches.",
            parameters: {
                type: "OBJECT",
                properties: {
                    artistName: { type: "STRING", description: "The artist's name." },
                    releaseTitle: { type: "STRING", description: "Title of the release (album, single, EP)." },
                    releaseType: { type: "STRING", description: "Type: single, EP, album, tour." },
                    releaseDate: { type: "STRING", description: "Release date in human-readable form." },
                    keyAngle: { type: "STRING", description: "The top-line story or hook (e.g. 'self-produced debut from Detroit')." },
                    pressRelease: { type: "STRING", description: "Full press release text to base pitches on (optional)." },
                    targetOutlets: {
                        type: "ARRAY",
                        items: { type: "STRING" },
                        description: "List of outlet types to target (e.g. 'indie blogs', 'hip-hop podcasts', 'local press')."
                    }
                },
                required: ["artistName", "releaseTitle", "keyAngle"]
            }
        }]
    }, async (args: { artistName: string; releaseTitle: string; releaseType?: string; releaseDate?: string; keyAngle: string; pressRelease?: string; targetOutlets?: string[] }) => {
        /**
         * Pillar 3 — Hype Machine: Generates personalized media pitches.
         * Each outlet type gets a unique angle, subject line, and email body.
         */
        const outlets = args.targetOutlets?.length
            ? args.targetOutlets
            : ['indie music blogs', 'hip-hop/R&B blogs', 'local press', 'music podcasts', 'playlist curators'];

        const releaseType = args.releaseType || 'release';
        const dateClause = args.releaseDate ? ` on ${args.releaseDate}` : '';

        const prompt = `You are a veteran music publicist writing personalized media pitches for a new ${releaseType}.

Artist: ${args.artistName}
Release: "${args.releaseTitle}"${dateClause}
Story Angle: ${args.keyAngle}
${args.pressRelease ? `\nPress Release:\n${args.pressRelease.slice(0, 1500)}` : ''}

Target outlet types: ${outlets.join(', ')}

For EACH outlet type, write a SHORT personalized pitch email with:
1. Subject line (specific to that outlet's style)
2. Email body (3-4 sentences max — concise, punchy, no filler)
3. A unique angle tailored to that outlet's audience

Format as JSON array with keys: outlet, subject, body, angle
Return only valid JSON, no markdown fences.`;

        try {
            const { AutonomousIntelligence } = await importWithRetry(() => import('@/services/intelligence/AutonomousIntelligence'));
            const { INTELLIGENCE_MODELS } = await importWithRetry(() => import('@/core/config/intelligence-models'));
            const raw = await AutonomousIntelligence.generateText(prompt, INTELLIGENCE_MODELS.TEXT.FAST);
            // Strip any markdown wrapping
            const cleaned = raw.replace(/```(?:json)?\n?/g, '').replace(/```$/g, '').trim();
            const pitches: Array<{ outlet: string; subject: string; body: string; angle: string }> = JSON.parse(cleaned);

            return {
                success: true,
                data: {
                    artistName: args.artistName,
                    releaseTitle: args.releaseTitle,
                    pitchCount: pitches.length,
                    pitches,
                    tip: "Personalize subject lines with the outlet's specific artist name or recent coverage for best open rates."
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: `Pitch generation failed: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "draft_press_release",
            description: "Draft a professional press release for a new music release, tour, or announcement.",
            parameters: {
                type: "OBJECT",
                properties: {
                    artist_name: { type: "STRING", description: "The artist's name." },
                    announcement_type: { type: "STRING", description: "e.g., Album Release, Tour, Signing" },
                    release_date: { type: "STRING", description: "Release date in human-readable form." },
                    key_details: { type: "ARRAY", items: { type: "STRING" }, description: "List of key facts or details." },
                    quotes: { type: "ARRAY", items: { type: "STRING" }, description: "Optional quotes from the artist or management." }
                },
                required: ["artist_name", "announcement_type", "key_details"]
            }
        }]
    }, async (args: { artist_name: string; announcement_type: string; release_date?: string; key_details: string[]; quotes?: string[] }) => {
        /**
         * Draft a professional press release.
         */
        const prompt = `You are an expert music publicist. Draft a professional press release for ${args.artist_name}.
Announcement Type: ${args.announcement_type}
${args.release_date ? `Release/Event Date: ${args.release_date}` : ''}
Key Details:
${args.key_details.map(d => `- ${d}`).join('\n')}
${args.quotes ? `Quotes to include:\n${args.quotes.map(q => `- "${q}"`).join('\n')}` : ''}

Format as a standard, ready-to-publish press release with a catchy headline, dateline, introduction, body paragraphs, and a boilerplate "About the Artist" section at the end.`;

        try {
            const { AutonomousIntelligence } = await importWithRetry(() => import('@/services/intelligence/AutonomousIntelligence'));
            const { INTELLIGENCE_MODELS } = await importWithRetry(() => import('@/core/config/intelligence-models'));
            const content = await AutonomousIntelligence.generateText(prompt, INTELLIGENCE_MODELS.TEXT.FAST);
            return {
                success: true,
                data: {
                    draft: content,
                    word_count: content.split(/\s+/).length
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: `Failed to draft press release: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "find_media_contacts",
            description: "Search the artist's own saved media list (bloggers, playlist curators, journalists) for contacts matching a genre, type and region. Only returns contacts the artist has already saved — it never invents contacts, and returns an empty result if the list has no match.",
            parameters: {
                type: "OBJECT",
                properties: {
                    genre: { type: "STRING", description: "Musical genre (e.g., Hip-Hop, Indie Rock, Electronic)." },
                    region: { type: "STRING", description: "Target region or 'Global'." },
                    contact_type: { type: "STRING", description: "e.g., Blog, Playlist, Magazine, Radio." }
                },
                required: ["genre", "contact_type"]
            }
        }]
    }, async (args: { genre: string; region?: string; contact_type: string }) => {
        /**
         * Find media contacts from the artist's own saved media list.
         *
         * ISSUE-1279: this previously prompted the model for "5 highly realistic
         * fictional media contacts" — including fabricated names, outlets and email
         * addresses — and returned them as success with no fictional flag, under a
         * tool description promising "curated media contacts". A user could have
         * pitched invented addresses believing them to be a real curated list.
         * Same policy as ISSUE-931 in the sibling publicist tools: the model must
         * never invent contact/identity facts. Reads the real user-scoped
         * `publicist_media_contacts` collection instead, and returns an honest
         * empty result rather than inventing entries when the list has no match.
         */
        try {
            const { auth, db } = await importWithRetry(() => import('@/services/firebase'));
            const { collection, getDocs } = await importWithRetry(() => import('firebase/firestore'));

            const userId = auth.currentUser?.uid;
            if (!userId) {
                return { success: false, error: 'Finding media contacts requires an authenticated user.' };
            }

            const snapshot = await getDocs(collection(db, 'users', userId, 'publicist_media_contacts'));
            const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name?: string; contact?: string; tags?: string[] }));

            const norm = (s: string) => s.trim().toLowerCase();
            const wanted = [args.genre, args.contact_type, args.region]
                .filter((v): v is string => typeof v === 'string' && v.trim().length > 0 && norm(v) !== 'global')
                .map(norm);

            const contacts = wanted.length === 0
                ? all
                : all.filter(c => {
                    const haystack = [...(c.tags || []), c.name || ''].map(norm);
                    // A contact matches when any requested criterion appears in its tags.
                    return wanted.some(w => haystack.some(h => h.includes(w) || w.includes(h)));
                });

            const criteria = `${args.genre} / ${args.contact_type}${args.region ? ` / ${args.region}` : ''}`;
            return {
                success: true,
                data: {
                    search_criteria: args,
                    results_count: contacts.length,
                    contacts,
                    message: contacts.length > 0
                        ? `Found ${contacts.length} saved media ${contacts.length === 1 ? 'contact' : 'contacts'} matching ${criteria}.`
                        : (all.length === 0
                            ? 'Your media list is empty. Add verified contacts with manage_media_list before pitching — contacts are never invented.'
                            : `None of your ${all.length} saved media contacts match ${criteria}.`)
                }
            };
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            return { success: false, error: `Failed to find media contacts: ${message}` };
        }
    })
    .withTool({
        functionDeclarations: [{
            name: "schedule_campaign_waterfall",
            description: "Schedule a campaign waterfall using the remote MCP backend.",
            parameters: {
                type: "OBJECT",
                properties: {
                    releaseId: { type: "STRING" },
                    campaignStartDate: { type: "STRING" },
                    budget: { type: "NUMBER" }
                },
                required: ["releaseId", "campaignStartDate"]
            }
        }]
    }, McpTools.schedule_campaign_waterfall)
    .withTool({
        functionDeclarations: [{
            name: "generate_playlist_pitch",
            description: "Generate a playlist pitch using the remote MCP backend.",
            parameters: {
                type: "OBJECT",
                properties: {
                    trackId: { type: "STRING" },
                    pitchAngle: { type: "STRING" }
                },
                required: ["trackId"]
            }
        }]
    }, McpTools.generate_playlist_pitch)
    .withAuthorizedTools([
        'create_campaign', 'write_press_release', 'generate_crisis_response',
        'generate_social_post', 'indii_image_gen', 'web_extract', 'credential_vault',
        'generate_pdf', 'generate_live_epk', 'pitch_media', 'draft_press_release',
        'find_media_contacts', 'schedule_campaign_waterfall', 'generate_playlist_pitch'
    ])
    .withDomainRetrieval(publicistRetrievalDeclarations, publicistRetrievalTools)
    .build();

// Freeze the schema to prevent cross-test contamination
